import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Eye, Inbox } from 'lucide-react';
import { listIncomingQuotes, ApiError } from '../api/client.js';
import type { IncomingQuoteListItem } from '../api/client.js';

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { label: string; className: string }> = {
    sent: { label: 'New', className: 'bg-blue-900/40 text-blue-400' },
    viewed: { label: 'Viewed', className: 'bg-gray-700 text-gray-300' },
    downloaded: { label: 'Downloaded', className: 'bg-green-900/40 text-green-400' },
  };
  const c = config[status] ?? { label: 'New', className: 'bg-blue-900/40 text-blue-400' };
  return (
    <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${c.className}`}>
      {c.label}
    </span>
  );
}

export function IncomingQuotesPage() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState<IncomingQuoteListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const data = await listIncomingQuotes();
        setQuotes(data.incomingQuotes);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Failed to load incoming quotes');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = quotes.filter((q) => {
    if (statusFilter && q.status !== statusFilter) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        (q.projectReference || '').toLowerCase().includes(term) ||
        (q.projectName || '').toLowerCase().includes(term) ||
        (q.sourceTenantName || '').toLowerCase().includes(term) ||
        (q.displayId || '').toLowerCase().includes(term)
      );
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-400 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-100">Incoming Quotes</h1>
        <p className="mt-1 text-sm text-gray-400">
          Quotes sent to you by manufacturers
        </p>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-900/30 px-4 py-3 text-sm text-red-400">{error}</div>
      )}

      {/* Filter Bar */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by project, manufacturer, or quote #..."
            className="w-full rounded-lg border border-gray-700 bg-gray-800 py-2 pl-9 pr-4 text-sm text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        >
          <option value="">All Statuses</option>
          <option value="sent">New</option>
          <option value="viewed">Viewed</option>
          <option value="downloaded">Downloaded</option>
        </select>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="rounded-lg border border-gray-800 bg-gray-900 p-12 text-center">
          <Inbox className="mx-auto mb-3 h-8 w-8 text-gray-600" />
          <p className="text-gray-500">
            {quotes.length === 0
              ? 'No incoming quotes yet. Manufacturers can send you quotes when you\'re connected through catalog subscriptions.'
              : 'No quotes match your filters.'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-800">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-800/50 text-left text-xs uppercase tracking-wider text-gray-400">
                <th className="px-4 py-3">From</th>
                <th className="px-4 py-3">Project</th>
                <th className="px-4 py-3">Quote #</th>
                <th className="px-4 py-3 text-center">Items</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3">Received</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((q) => (
                <tr
                  key={q.id}
                  className={`border-b border-gray-800 hover:bg-gray-800/30 ${q.status === 'sent' ? 'bg-blue-950/10' : ''}`}
                >
                  <td className="px-4 py-3 text-sm font-medium text-gray-200">
                    {q.sourceTenantName}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-300">
                    {q.projectReference || q.projectName || '\u2014'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-400">
                    {q.displayId || '\u2014'}
                  </td>
                  <td className="px-4 py-3 text-center text-sm text-gray-400">
                    {q.lineItemCount}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-100">
                    {formatCurrency(q.totalMsrp)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {formatDate(q.sentAt)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <StatusBadge status={q.status} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => navigate(`/incoming-quotes/${q.id}`)}
                      className="inline-flex items-center gap-1 rounded p-1.5 text-gray-500 hover:bg-gray-800 hover:text-gray-300"
                      title="View"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
